'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Thermometer, Snowflake, Route } from 'lucide-react';
import type { CurrentObservation } from '@/lib/fmi';
import type { AuroraData } from '@/lib/aurora';

interface WeatherResponse {
  levi: CurrentObservation;
  yllas: CurrentObservation;
}

export default function LiveDataStrip({
  snowTemplate = '{depth}cm snow',
  auroraTemplate = 'Aurora: {label}',
  roadsLabel = 'Roads: Good',
}: {
  snowTemplate?: string;
  auroraTemplate?: string;
  roadsLabel?: string;
}) {
  const [weather, setWeather] = useState<WeatherResponse | null>(null);
  const [aurora, setAurora] = useState<AuroraData | null>(null);
  const [roadOk, setRoadOk] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/api/weather').then((r) => (r.ok ? r.json() : null)),
      fetch('/api/aurora').then((r) => (r.ok ? r.json() : null)),
      fetch('/api/road-conditions').then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([w, a, road]) => {
        if (w) setWeather(w);
        if (a) setAurora(a);
        if (road) setRoadOk(true);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-[#F5F0E8] py-4">
        <div className="max-w-5xl mx-auto px-4 flex justify-center">
          <div className="h-5 w-64 bg-[#e8dbb8] rounded animate-pulse" />
        </div>
      </div>
    );
  }

  const segments: { href: string; content: React.ReactNode }[] = [];

  if (weather) {
    const leviTemp = weather.levi.temperature !== null ? Math.round(weather.levi.temperature) : '--';
    const leviSnow = weather.levi.snowDepth !== null && weather.levi.snowDepth > 0 ? snowTemplate.replace('{depth}', String(Math.round(weather.levi.snowDepth))) : null;
    const yllasTemp = weather.yllas.temperature !== null ? Math.round(weather.yllas.temperature) : '--';
    const yllasSnow = weather.yllas.snowDepth !== null && weather.yllas.snowDepth > 0 ? snowTemplate.replace('{depth}', String(Math.round(weather.yllas.snowDepth))) : null;

    segments.push({
      href: '/weather',
      content: (
        <span className="flex items-center gap-1.5">
          <Thermometer className="w-3.5 h-3.5 text-[#9B7B3A]" />
          <span>Levi: {leviTemp}°C{leviSnow ? ` / ${leviSnow}` : ''}</span>
        </span>
      ),
    });
    segments.push({
      href: '/weather',
      content: (
        <span className="flex items-center gap-1.5">
          <Snowflake className="w-3.5 h-3.5 text-[#9B7B3A]" />
          <span>Ylläs: {yllasTemp}°C{yllasSnow ? ` / ${yllasSnow}` : ''}</span>
        </span>
      ),
    });
  }

  if (aurora) {
    segments.push({
      href: '/aurora',
      content: (
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: aurora.hex }}
          />
          <span>{auroraTemplate.replace('{label}', aurora.label)}</span>
        </span>
      ),
    });
  }

  if (roadOk !== null) {
    segments.push({
      href: '/road-conditions',
      content: (
        <span className="flex items-center gap-1.5">
          <Route className="w-3.5 h-3.5 text-[#9B7B3A]" />
          <span>{roadsLabel}</span>
        </span>
      ),
    });
  }

  if (segments.length === 0) return null;

  return (
    <div className="bg-[#F5F0E8] py-4">
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-[#1A1A2E]">
          {segments.map((seg, i) => (
            <span key={i} className="flex items-center gap-2">
              {i > 0 && (
                <span className="hidden sm:inline text-[#9B7B3A]/30 -ml-2 mr-0">|</span>
              )}
              <Link
                href={seg.href}
                className="hover:text-[#9B7B3A] transition-colors"
              >
                {seg.content}
              </Link>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
